import React, { ChangeEvent } from "react";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { FormControl } from "../FormControl";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { validateVariableName } from "../../../rule-engine-core/Common/DataValidationUtilities";
import { TextField } from "@mui/material";
import { ObjectDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { NumberDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import UIChart from "../../../CommonUI/BuildlingBlocks/UIChart";
import { chartNonSeries } from "../Testing/ChartSameple";
import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

export class PieChartFormControl extends FormControl {
    readonly __type: string = "Pie Chart";

    chartType: string;
    innerRadius: number;

    objectPath?: string;
    dataName?: string;
    label?: string;

    isDonut: boolean;

    showLabel: boolean;
    showToolTip: boolean;
    show3D: boolean;

    colorType: string;

    legendPosition: string;

    customRadius: boolean;

    constructor() {
        super();
        this.chartType = "PieChart";
        this.label = "Configure Label";
        this.showLabel = true;
        this.showToolTip = true;
        this.show3D = false;
        this.colorType = "Random";
        this.legendPosition = "No";
        this.customRadius = false;
        this.innerRadius = 40;
        this.isDonut = false;

        this.updateDataDefinition = this.updateDataDefinition.bind(this);
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (!this.dataName) {
            issues.push(new ValidationIssue("Data name is required", undefined, this.id));
        } else {
            let varIssue = validateVariableName(this.dataName);

            if (varIssue) {
                varIssue.dataName = this.dataName;
                varIssue.elementId = this.id;
                issues.push(varIssue);
            }
        }

        return issues;
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return undefined;
    }

    getDataDefinition(): IDataDefinition | undefined {
        if (!this.dataName) return;
        let chartDataDef = new ObjectDataDefinition(this.dataName);
        chartDataDef.list = true;
        const labelDef = new StringDataDefinition("label");
        chartDataDef.fields.push(labelDef);
        const valueDef = new NumberDataDefinition("value");
        chartDataDef.fields.push(valueDef);
        if (this.colorType === "Custom") {
            const colorDef = new StringDataDefinition("color");
            chartDataDef.fields.push(colorDef);
        }

        if (this.customRadius) {
            const radiusDef = new NumberDataDefinition("radius");
            chartDataDef.fields.push(radiusDef);
        }

        return chartDataDef;
    }

    updateDataDefinition(previous?: string) {
        const dataDef = this.getFormContext()?.form.getInputDefinition();
        const findKey = previous ? previous : this.dataName!;
        if (dataDef) {
            let tempFields = dataDef.fields;
            if (findKey) {
                tempFields = dataDef.fields.filter((field) => field.name !== findKey);
            }
            const chartDataDef = this.getDataDefinition();
            if (chartDataDef) {
                tempFields.push(chartDataDef);
            }

            Reflect.set(dataDef, "fields", tempFields);
        }
        StateManager.changed(this);
    }

    updateChartData(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const value = e.target.value;
        const previous = this.dataName;
        this.dataName = value;
        this.updateDataDefinition(previous);
    }

    getChartDefSampleValue(): any[] {
        const dataDef = this.getFormContext()?.form.getInputDefinition();
        if (!dataDef || !this.dataName) {
            return [];
        }
        const data = Reflect.get(dataDef.value, this.dataName) as any[];
        if (!data) return [];
        return data.map((dataItem) => {
            if (dataItem.color) {
                return {
                    label: dataItem.label,
                    value: dataItem.value,
                    color: dataItem.color,
                };
            } else {
                return {
                    label: dataItem.label,
                    value: dataItem.value,
                };
            }
        });
    }

    getChartData() {
        const dataDef = this.getFormContext()?.form.getInputDefinition();
        if (!dataDef || !this.dataName) {
            return chartNonSeries;
        }
        const data = Reflect.get(dataDef.value, this.dataName) as any[];
        if (!data) return chartNonSeries;
        return data;
    }

    render() {
        return (
            <>
                {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                {this.getChartData() && this.getChartData().length > 0 && (
                    <div data-testid={this.dataName}>
                        <UIChart
                            options={{
                                data: this.getChartData(),
                                chartType: this.chartType,
                                legendPosition: this.legendPosition?.toLowerCase(),
                                colorType: this.colorType,
                                showToolTip: this.showToolTip,
                                showLabel: this.showLabel,
                                show3D: this.show3D,
                                isDonut: this.isDonut,
                                innerRadius: this.innerRadius,
                                customRadius: this.customRadius,
                            }}
                        />
                    </div>
                )}
            </>
        );
    }

    renderEditUI(): JSX.Element | null {
        return (
            <UISpacedBoxContent>
                <PropertyEditorText
                    editObject={this}
                    label={"Label"}
                    propertyName="label"
                />

                <TextField
                    label="Dataname"
                    value={this.dataName}
                    onChange={(e) => this.updateChartData(e)}
                    variant="outlined"
                />

                <PropertyEditorBoolean
                    label="Show Donut"
                    propertyName="isDonut"
                    editObject={this}
                />

                <PropertyEditorCollapse
                    editObject={this}
                    collapseLogicOrField={"isDonut"}
                >
                    <PropertyEditorInteger
                        editObject={this}
                        label={"Inner Radius"}
                        propertyName="innerRadius"
                    />
                </PropertyEditorCollapse>

                <PropertyEditorSelect
                    label="Show Legend"
                    options={["No", "Top", "Bottom", "Left", "Right"]}
                    propertyName="legendPosition"
                    editObject={this}
                />

                <PropertyEditorSelect
                    label="Color Schema"
                    options={["Custom", "Random"]}
                    propertyName="colorType"
                    editObject={this}
                    onChange={this.updateDataDefinition}
                />

                <PropertyEditorBoolean
                    label="Custom Radius"
                    propertyName="customRadius"
                    editObject={this}
                    onChange={this.updateDataDefinition}
                />

                <PropertyEditorBoolean
                    label="Show 3D"
                    propertyName="show3D"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Show Tooltip"
                    propertyName="showToolTip"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Show Labels"
                    propertyName="showLabel"
                    editObject={this}
                />

                <PropertyEditorBoolean
                    label="Allow Actions"
                    propertyName="allowActions"
                    editObject={this}
                />
            </UISpacedBoxContent>
        );
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "chartType",
            "innerRadius",
            "objectPath",
            "dataName",
            "label",
            "isDonut",
            "showLabel",
            "showToolTip",
            "show3D",
            "colorType",
            "legendPosition",
            "customRadius",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "chartType",
            "innerRadius",
            "objectPath",
            "dataName",
            "label",
            "isDonut",
            "showLabel",
            "showToolTip",
            "show3D",
            "colorType",
            "legendPosition",
            "customRadius",
        );
    }
}
